import moment from 'moment'

import { FibuUtils } from './fibu-utils'
import { ObjUtils } from './obj-utils'
import { IndexUtils } from './index-utils'
import { Ix2044Utils } from './ix2044-utils'
import { ContractUtils } from './contract-utils'

import { reportAccountTypeMap } from 'helpers/report-account-type-map'


export class Utils {
    static instance: Utils
    licenses: Array<any> = []
    user: any = null

    // sub utility classes
    fibu!: FibuUtils
    obj!: ObjUtils
    index!: IndexUtils
    ix2044!: Ix2044Utils
    contract!: ContractUtils

    constructor() {
        if (Utils.instance) {
            return Utils.instance
        }

        this.fibu = new FibuUtils(this)
        this.obj = new ObjUtils(this)
        this.index = new IndexUtils(this)
        this.ix2044 = new Ix2044Utils(this)
        this.contract = new ContractUtils(this)

        Utils.instance = this
        return this
    }

    // setters
    setLicenses(licenses: Array<any>) {
        this.licenses = licenses
    }

    setUser(user: any) {
        this.user = user
    }

    // utility functions start here
    isLicenseInstalled(license: any) {
        return this.licenses.indexOf(license) !== -1
    }

    currUserId = () => {
        return this.user?.ixUserId || null
    }

    currNcremUserId = () => {
        return this.user?.ncremUserId || null
    }

    moduleRightAvailable = (moduleRight: string, rightWanted: string) => {
        console.log('moduleRightAvailable: ', '.' + moduleRight + '.', '.' + rightWanted + '.')
        return Array.from(rightWanted).includes(moduleRight)
    }

    sysdate = () => {
        return moment().format('YYYY-MM-DD')
    }

    systime = () => {
        return moment().format('HH:mm')
    }

    systimestamp = () => {
        return moment().format('YYYY-MM-DD HH:mm:ss.SSS')
    }

    lastDayOfMonth = (date: string) => {
        return moment(date, 'YYYY-MM-DD').endOf('month').format('YYYY-MM-DD')
    }

    bitwiseAnd = (lhs: any, rhs: any, expected: any) => {
        return (lhs & rhs) === expected
    }

    bitwiseAndNot = (lhs: any, rhs: any, notExpected: any) => {
        return (lhs & rhs) !== notExpected
    }

    addDate = (date: string, leadTime: number, leadTimeType: number) => {
        if (!date) { return date }

        if (leadTimeType === 0) {
            return moment(date, 'YYYY-MM-DD').add(leadTime, 'months').format('YYYY-MM-DD')
        } else if (leadTimeType === 1) {
            return moment(date, 'YYYY-MM-DD').add(leadTime, 'days').format('YYYY-MM-DD')
        } else {
            return date
        }
    }

    diffDate = (date1: string, date2: string, leadTimeType: number) => {
        if (leadTimeType === 0) {
            return moment(date1, 'YYYY-MM-DD').diff(moment(date2, 'YYYY-MM-DD'), 'months')
        } else if (leadTimeType === 1) {
            return moment(date1, 'YYYY-MM-DD').diff(moment(date2, 'YYYY-MM-DD'), 'days')
        } else {
            return 0
        }
    }

    diffDays = (date1: string, date2: string) => {
        return this.diffDate(date1, date2, 1)
    }

    diffMonths = (date1: string, date2: string) => {
        return this.diffDate(date1, date2, 0)
    }

    isOneInRange = (value: any, dataItem: any) => {
       return dataItem.find((x: any) => value.includes(x))
    }

    isInRange = (value: any, dataItem: any) => {
        return value?.includes(dataItem)
    }

    getBtKreisIcon = (row: any) => {
        const iconMap: any = {
            22: 'home',
            1: 'showpanel',
            2: 'box',
        }
        return iconMap[row.TYPID]
    }

    UserCanEditObjParams = () => { return true }

    CanEditBank = () => { return true }

    join = (sep: string, ...args: any[]) => {
        return args.join(sep)
    }

    multiply = (...args: any[]) => {
        return args.reduce((acc, val) => acc * val, 1)
    }

    addPercent = (value: number, percent: number) => {
        return value + (value * percent / 100)
    }

    add = (...args: any[]) => {
        return args.reduce((acc, val) => acc + val, 0)
    }

    isNull = (value: any) => {
        return value === null || value === undefined || (typeof value === 'number' && Math.abs(value) < 0.005)
    }

    calcObligoBA30(SUMMEBRUTTO: number, SUMMENETTO: number, OBLIGO: number, OPTIONSATZ: number) {
        if (this.isNull(SUMMENETTO))
            return 0

        return (SUMMEBRUTTO - SUMMENETTO) * (OBLIGO / SUMMENETTO) * (1 - OPTIONSATZ/100)
    }

    addDateTokens = (prefix: string, fieldName: any) => {
        return [
            {placeholder: `$${prefix}TT`, description: 'global:placeholder.dayTwoDigits', prefix: prefix, fieldName: fieldName},
            {placeholder: `$${prefix}WD`, description: 'global:placeholder.weekday', prefix: prefix, fieldName: fieldName},
            {placeholder: `$${prefix}MM`, description: 'global:placeholder.monthTwoDigit', prefix: prefix, fieldName: fieldName},
            {placeholder: `$${prefix}MMM`, description: 'global:placeholder.monthShortName', prefix: prefix, fieldName: fieldName},
            {placeholder: `$${prefix}MMMM`, description: 'global:placeholder.monthFullName', prefix: prefix, fieldName: fieldName},
            {placeholder: `$${prefix}JJ`, description: 'global:placeholder.yearTwoDigits', prefix: prefix, fieldName: fieldName},
            {placeholder: `$${prefix}JJJJ`, description: 'global:placeholder.yearFourDigits', prefix: prefix, fieldName: fieldName},
        ]
    }

    resolveDateFormat = (fieldValue: string, tokenArrays: any[], values: any) => {
        const regexes = tokenArrays?.map((item: any) => (new RegExp(`\\$(${item.prefix}TT|${item.prefix}WD|${item.prefix}MMMM|${item.prefix}MMM|${item.prefix}MM|${item.prefix}JJJJ|${item.prefix}JJ)`)))
        const regex = new RegExp(`\\$(${regexes.map((r: any) => r.source.match(/\((.*)\)/)[1]).join('|')})`,'g')

        const handleFormat = (dateValue: string, format: string, prefix: string, suffix: string) => {
            return dateValue
                ? moment(dateValue).format(format)
                : prefix
                    ? `$${prefix}${suffix}`
                    : moment().format(format)
        }

        let typeDateFormat: any = {}

        tokenArrays.forEach((item: any) => {
            const dateValue = (values as any)[item.fieldName]
            typeDateFormat = {
                ...typeDateFormat,
                [`$${item.prefix}TT`]: handleFormat(dateValue, 'DD', item.prefix, 'TT'),
                [`$${item.prefix}WD`]: handleFormat(dateValue, 'dddd', item.prefix, 'WD'),
                [`$${item.prefix}MMMM`]: handleFormat(dateValue, 'MMMM', item.prefix, 'MMMM'),
                [`$${item.prefix}MMM`]: handleFormat(dateValue, 'MMM', item.prefix, 'MMM'),
                [`$${item.prefix}MM`]: handleFormat(dateValue, 'MM', item.prefix, 'MM'),
                [`$${item.prefix}JJJJ`]: handleFormat(dateValue, 'YYYY', item.prefix, 'JJJJ'),
                [`$${item.prefix}JJ`]: handleFormat(dateValue, 'YY', item.prefix, 'JJ'),
            }
        }) 

        return fieldValue.replace(regex, (m: any) => typeDateFormat[m])
    }

    checkBoxDateState(checkboxValue: boolean, dateValue: Date, textForStateValid: string, textForStateInvalid: string, t: any) {
        if (this.isNull(dateValue) || !dateValue || !checkboxValue)
            return t(textForStateInvalid)

        return t(textForStateValid, { dateVal: moment(dateValue).format(t('global:format.datetimeFormat_date').replace('dd', 'DD'))})
    }

    strFirstXChars = (value: any, x: number) => {
        if (!value) return ''
        return value.toString().slice(0, x)
    }

    strLastXChars = (value: any, x: number) => {
        if (!value) return ''
        return value.toString().slice(-x)
    }

    strSubstring = (value: any, start: number, end: number) => {
        if (!value) return ''
        return value.toString().slice(start, end)
    }

    reportAccountTypeMap = reportAccountTypeMap
}
